import React, { useMemo, useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import AppLayout from '../components/AppLayout';
import api from '../services/api';
import { routes } from '../lib/routes';
import './AdminUsuarios.css';

const PERFIL_LABELS = {
  admin: 'Administrador',
  gestor: 'Gestor',
  analista: 'Analista',
  responsavel_tecnico: 'Responsável técnico',
  cidadao: 'Cidadão',
  empresa: 'Empresa',
};

function labelPerfil(tipo) {
  if (!tipo) return '—';
  return PERFIL_LABELS[tipo] || tipo.replace(/_/g, ' ');
}

function badgeStatusClass(status) {
  if (status === 'ativo') return 'admin-users-badge admin-users-badge--ativo';
  if (status === 'inativo') return 'admin-users-badge admin-users-badge--inativo';
  if (status === 'pendente') return 'admin-users-badge admin-users-badge--pendente';
  return 'admin-users-badge admin-users-badge--inativo';
}

export default function AdminUsuarios() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    email: '',
    password: '',
    cpf_cnpj: '',
    nome: '',
    telefone: '',
    tipo: 'analista',
    status: 'ativo',
  });
  const [filters, setFilters] = useState({ q: '', tipo: '', status: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ nome: '', telefone: '', tipo: 'analista', status: 'ativo' });
  const [sort, setSort] = useState({ field: 'created_at', direction: 'desc' });
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const { data: me } = useQuery('me', () => api.get('/usuarios/me').then((r) => r.data), { retry: false });
  const { data: usuarios = [], isLoading } = useQuery('usuarios-admin', () => api.get('/usuarios').then((r) => r.data), {
    retry: false,
  });

  useEffect(() => {
    setPage(1);
  }, [filters.q, filters.tipo, filters.status]);

  const createMutation = useMutation((payload) => api.post('/usuarios', payload).then((r) => r.data), {
    onSuccess: () => {
      toast.success('Usuário cadastrado com sucesso.');
      setForm({
        email: '',
        password: '',
        cpf_cnpj: '',
        nome: '',
        telefone: '',
        tipo: 'analista',
        status: 'ativo',
      });
      queryClient.invalidateQueries('usuarios-admin');
    },
    onError: () => toast.error('Erro ao cadastrar usuário.'),
  });

  const statusMutation = useMutation(
    ({ id, status }) => api.patch(`/usuarios/${id}/status`, { status }).then((r) => r.data),
    {
      onSuccess: () => queryClient.invalidateQueries('usuarios-admin'),
      onError: () => toast.error('Erro ao atualizar status.'),
    }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => api.put(`/usuarios/${id}`, payload).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Usuário atualizado com sucesso.');
        setEditingUser(null);
        queryClient.invalidateQueries('usuarios-admin');
      },
      onError: () => toast.error('Erro ao atualizar usuário.'),
    }
  );

  const usersCount = useMemo(
    () => ({
      total: usuarios.length,
      ativos: usuarios.filter((u) => u.status === 'ativo').length,
      inativos: usuarios.filter((u) => u.status === 'inativo').length,
    }),
    [usuarios]
  );

  const filteredUsers = useMemo(() => {
    const result = usuarios.filter((u) => {
      const matchQ =
        !filters.q ||
        u.nome?.toLowerCase().includes(filters.q.toLowerCase()) ||
        u.email?.toLowerCase().includes(filters.q.toLowerCase());
      const matchTipo = !filters.tipo || u.tipo === filters.tipo;
      const matchStatus = !filters.status || u.status === filters.status;
      return matchQ && matchTipo && matchStatus;
    });

    result.sort((a, b) => {
      const av = a?.[sort.field] ?? '';
      const bv = b?.[sort.field] ?? '';
      if (av < bv) return sort.direction === 'asc' ? -1 : 1;
      if (av > bv) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [usuarios, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page]);

  const handleSubmit = (event) => {
    event.preventDefault();
    createMutation.mutate(form);
  };

  const toggleSort = (field) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      nome: user.nome || '',
      telefone: user.telefone || '',
      tipo: user.tipo || 'analista',
      status: user.status || 'ativo',
    });
  };

  const handleEditSubmit = (event) => {
    event.preventDefault();
    if (!editingUser) return;
    updateMutation.mutate({ id: editingUser.id, payload: editForm });
  };

  if (me && me.tipo !== 'admin') {
    return <Navigate to={routes.dashboard} replace />;
  }

  return (
    <AppLayout
      title="Administração de usuários"
      subtitle="Gestão de perfis, acessos e permissões do sistema."
      actions={
        <Link to={routes.dashboard} className="sispro-btn sispro-btn--secondary sispro-btn--sm">
          Dashboard
        </Link>
      }
    >
      <div className="admin-users-page">
        <p className="admin-users-intro">Resumo da base de usuários e cadastro de novas contas.</p>

        <div className="admin-users-stats" role="list">
          <article className="admin-users-stat admin-users-stat--total" role="listitem">
            <p className="admin-users-stat-label">Total</p>
            <p className="admin-users-stat-value">{isLoading ? '…' : usersCount.total}</p>
          </article>
          <article className="admin-users-stat admin-users-stat--ativos" role="listitem">
            <p className="admin-users-stat-label">Ativos</p>
            <p className="admin-users-stat-value">{isLoading ? '…' : usersCount.ativos}</p>
          </article>
          <article className="admin-users-stat admin-users-stat--inativos" role="listitem">
            <p className="admin-users-stat-label">Inativos</p>
            <p className="admin-users-stat-value">{isLoading ? '…' : usersCount.inativos}</p>
          </article>
        </div>

        <section className="panel admin-users-panel" aria-labelledby="admin-novo-heading">
          <div className="admin-users-section-head">
            <h2 id="admin-novo-heading">Novo usuário</h2>
          </div>
          <div className="admin-users-section-body">
            <form onSubmit={handleSubmit}>
              <div className="admin-users-form-grid">
                <div className="admin-users-field">
                  <label htmlFor="au-nome">Nome completo</label>
                  <input
                    id="au-nome"
                    placeholder="Nome e apelido"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-users-field">
                  <label htmlFor="au-email">E-mail</label>
                  <input
                    id="au-email"
                    placeholder="email@dominio.gov.br"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-users-field">
                  <label htmlFor="au-senha">Senha inicial</label>
                  <input
                    id="au-senha"
                    placeholder="Mínimo recomendado: 8 caracteres"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-users-field">
                  <label htmlFor="au-doc">CPF / CNPJ</label>
                  <input
                    id="au-doc"
                    placeholder="Somente números"
                    value={form.cpf_cnpj}
                    onChange={(e) => setForm({ ...form, cpf_cnpj: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-users-field">
                  <label htmlFor="au-tel">Telefone</label>
                  <input
                    id="au-tel"
                    placeholder="Opcional"
                    value={form.telefone}
                    onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  />
                </div>
                <div className="admin-users-field">
                  <label htmlFor="au-perfil">Perfil</label>
                  <select id="au-perfil" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                    <option value="admin">Administrador</option>
                    <option value="gestor">Gestor</option>
                    <option value="analista">Analista</option>
                    <option value="responsavel_tecnico">Responsável técnico</option>
                    <option value="cidadao">Cidadão</option>
                    <option value="empresa">Empresa</option>
                  </select>
                </div>
              </div>
              <div className="admin-users-form-actions">
                <p className="admin-users-form-hint">O usuário poderá acessar o sistema com o e-mail e a senha definidos aqui.</p>
                <button type="submit" className="sispro-btn sispro-btn--primary" disabled={createMutation.isLoading}>
                  {createMutation.isLoading ? 'Salvando…' : 'Cadastrar usuário'}
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="panel admin-users-panel" aria-labelledby="admin-lista-heading">
          <div className="admin-users-section-head">
            <h2 id="admin-lista-heading">Usuários cadastrados</h2>
          </div>
          <div className="admin-users-section-body">
            <p className="admin-users-meta">
              Sessão atual: <strong>{me?.nome}</strong> ({labelPerfil(me?.tipo)})
            </p>

            <div className="admin-users-filters">
              <div className="admin-users-filter-field">
                <label htmlFor="au-busca">Pesquisa</label>
                <input
                  id="au-busca"
                  placeholder="Nome ou e-mail"
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                />
              </div>
              <div className="admin-users-filter-field">
                <label htmlFor="au-f-tipo">Perfil</label>
                <select id="au-f-tipo" value={filters.tipo} onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}>
                  <option value="">Todos os perfis</option>
                  <option value="admin">Administrador</option>
                  <option value="gestor">Gestor</option>
                  <option value="analista">Analista</option>
                  <option value="responsavel_tecnico">Responsável técnico</option>
                  <option value="cidadao">Cidadão</option>
                  <option value="empresa">Empresa</option>
                </select>
              </div>
              <div className="admin-users-filter-field">
                <label htmlFor="au-f-status">Estado</label>
                <select id="au-f-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <option value="">Todos os estados</option>
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                  <option value="pendente">Pendente</option>
                </select>
              </div>
            </div>

            {isLoading ? <div className="admin-users-loading">Carregando lista…</div> : null}

            {!isLoading ? (
              <>
                <div className="admin-users-table-wrap">
                  <table className="admin-users-table">
                    <thead>
                      <tr>
                        <th scope="col" className="sortable" onClick={() => toggleSort('nome')} title="Ordenar por nome">
                          Nome
                        </th>
                        <th scope="col" className="sortable" onClick={() => toggleSort('email')} title="Ordenar por e-mail">
                          E-mail
                        </th>
                        <th scope="col">CPF / CNPJ</th>
                        <th scope="col" className="sortable" onClick={() => toggleSort('tipo')} title="Ordenar por perfil">
                          Perfil
                        </th>
                        <th scope="col" className="sortable" onClick={() => toggleSort('status')} title="Ordenar por estado">
                          Estado
                        </th>
                        <th scope="col">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.nome}</td>
                          <td>{user.email}</td>
                          <td>{user.cpf_cnpj}</td>
                          <td>{labelPerfil(user.tipo)}</td>
                          <td>
                            <span className={badgeStatusClass(user.status)}>{user.status}</span>
                          </td>
                          <td>
                            <div className="admin-users-table-actions">
                              <button type="button" className="sispro-btn sispro-btn--secondary sispro-btn--sm" onClick={() => startEdit(user)}>
                                Editar
                              </button>
                              <button
                                type="button"
                                className="sispro-btn sispro-btn--ghost sispro-btn--sm"
                                onClick={() => statusMutation.mutate({ id: user.id, status: user.status === 'ativo' ? 'inativo' : 'ativo' })}
                                disabled={statusMutation.isLoading}
                              >
                                {user.status === 'ativo' ? 'Inativar' : 'Ativar'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="admin-users-pagination">
                  <span className="admin-users-pagination-info">
                    Página {page} de {totalPages} — {filteredUsers.length} resultado(s)
                  </span>
                  <button
                    type="button"
                    className="sispro-btn sispro-btn--secondary sispro-btn--sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className="sispro-btn sispro-btn--secondary sispro-btn--sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Próxima
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </section>
      </div>

      {editingUser ? (
        <div
          className="admin-users-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditingUser(null);
          }}
        >
          <div className="admin-users-modal panel">
            <div className="admin-users-modal-head">
              <h3 id="admin-modal-title">Editar usuário</h3>
              <p>{editingUser.email}</p>
            </div>
            <div className="admin-users-modal-body">
              <form onSubmit={handleEditSubmit} className="admin-users-modal-form">
                <div className="admin-users-field">
                  <label htmlFor="au-e-nome">Nome</label>
                  <input id="au-e-nome" value={editForm.nome} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} required />
                </div>
                <div className="admin-users-field">
                  <label htmlFor="au-e-tel">Telefone</label>
                  <input id="au-e-tel" value={editForm.telefone} onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })} />
                </div>
                <div className="admin-users-field">
                  <label htmlFor="au-e-tipo">Perfil</label>
                  <select id="au-e-tipo" value={editForm.tipo} onChange={(e) => setEditForm({ ...editForm, tipo: e.target.value })}>
                    <option value="admin">Administrador</option>
                    <option value="gestor">Gestor</option>
                    <option value="analista">Analista</option>
                    <option value="responsavel_tecnico">Responsável técnico</option>
                    <option value="cidadao">Cidadão</option>
                    <option value="empresa">Empresa</option>
                  </select>
                </div>
                <div className="admin-users-field">
                  <label htmlFor="au-e-status">Estado</label>
                  <select id="au-e-status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                    <option value="pendente">Pendente</option>
                  </select>
                </div>
                <div className="admin-users-modal-actions">
                  <button type="button" className="sispro-btn sispro-btn--secondary" onClick={() => setEditingUser(null)}>
                    Cancelar
                  </button>
                  <button type="submit" className="sispro-btn sispro-btn--primary" disabled={updateMutation.isLoading}>
                    {updateMutation.isLoading ? 'Salvando…' : 'Salvar alterações'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
}
